import auth from './auth'

import {
  AppRoute,
} from '@kaidam/fc-shared/src/types/frontend/router'

import {
  FILESTORE_TYPE_PRIVATE,
} from '@kaidam/fc-shared/src/constants'

import {
  RootState,
  ThunkDispatcher,
} from '../types/store'

import {
  getRoleRedirectHandler,
  rbacRedirectHandler,
} from './redirects'

import nopressActions from '../store/modules/nopress'
import nopressSelectors from '../store/selectors/nopress'

const routes: AppRoute<RootState>[] = [
  {
    name: 'home',
    path: '/',
    auth: auth.guest,
  },
  {
    name: 'notfound',
    path: '/notfound',
  },
  {
    name: 'login',
    path: '/login',
    auth: auth.guest,
  },
  {
    name: 'register',
    path: '/register',
    auth: auth.guest,
  },
  {
    name: 'logout',
    path: '/logout',
  },
  {
    name: 'reset_password',
    path: '/reset_password',
  },
  {
    name: 'settings',
    path: '/settings',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      redirectName: 'settings.password',
    }),
    children: [{
      name: 'password',
      path: '/password',
    }, {
      name: 'project',
      path: '/project',
      redirect: getRoleRedirectHandler({
        redirectName: 'settings.project',
        roleNames: ['project:admin'],
      }),
    }, {
      name: 'ai',
      path: '/ai',
      redirect: getRoleRedirectHandler({
        redirectName: 'settings.ai',
        roleNames: ['project:admin'],
      }),
    }]
  },
  {
    name: 'dashboard',
    path: '/dashboard',
    auth: auth.user,
  },
  {
    name: 'operationshub',
    path: '/operationshub',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['knowledgebase:reader'],
      redirectName: 'operationshub.sections',
    }),
    children: [{
      name: 'sections',
      path: '/sections',
    }, {
      name: 'section',
      path: '/section/:section',
      children: [{
        name: 'article',
        path: '/article/:article', 
        children: [{
          name: 'page',
          path: '/page/:page', 
        }]
      }]
    }, {
      name: 'proxy',
      path: '/proxy/:type',
    }],
  },
  {
    name: 'support',
    path: '/support/:type',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['support:user'],
      redirectName: 'support.list',
    }),
    children: [{
      name: 'list',
      path: '/list',
    }, {
      name: 'thread',
      path: '/thread/:id',
    }]
  },
  {
    name: 'reporting',
    path: '/reporting',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['reporting:viewer'],
      redirectName: 'reporting.org',
      orgParam: 'orgid',
    }),
    children: [{
      name: 'org',
      path: '/org/:orgid',
    }]
  },
  {
    name: 'coconut_ppc_payments',
    path: '/coconut_ppc_payments',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['coconut_ppc_payments:customer'],
      redirectName: 'coconut_ppc_payments.org',
      orgParam: 'orgid',
    }),
    children: [{
      name: 'org',
      path: '/org/:orgid',
    }]
  },
  {
    name: 'crm',
    path: '/crm',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['crm:reader'],
      redirectName: 'crm.contacts',
    }),
    children: [{
      name: 'tags',
      path: '/tags',
      redirect: getRoleRedirectHandler({
        roleNames: ['crm:reader'],
        redirectName: 'crm.tags.list',
      }),
      children: [{
        name: 'list',
        path: '/list',
      }, {
        name: 'edit',
        path: '/edit/:tagid',
      }, {
        name: 'delete',
        path: '/delete/:tagid',
      }]
    }, {
      name: 'contacts',
      path: '/contacts',
      redirect: getRoleRedirectHandler({
        roleNames: ['crm:reader'],
        redirectName: 'crm.contacts',
        orgParam: 'orgid',
        scopeParam: 'scope',
      })
    }, {
      name: 'contact',
      path: '/contact/:orgid/:contactid',
    }, {
      name: 'events',
      path: '/events',
      redirect: getRoleRedirectHandler({
        roleNames: ['crm:reader'],
        redirectName: 'crm.events',
        orgParam: 'orgid',
        scopeParam: 'scope',
      }),
    }, {
      name: 'imports',
      path: '/imports',
      redirect: getRoleRedirectHandler({
        roleNames: ['crm:reader'],
        redirectName: 'crm.imports.list',
      }),
      children: [{
        name: 'list',
        path: '/list',
      }, {
        name: 'new',
        path: '/new',
      }, {
        name: 'view',
        path: '/view/:id',
        children: [{
          name: 'job',
          path: '/job/:jobid',
        }]
      }]
    }]
  },
  {
    name: 'astonish',
    path: '/astonish',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      redirectName: 'astonish.dashboard',
    }),
    children: [{
      name: 'dashboard',
      path: '/dashboard',
      redirect: getRoleRedirectHandler({
        roleNames: ['astonish:campaign_reader'],
        redirectName: 'astonish.dashboard',
        orgParam: 'orgid',
        scopeParam: 'scope',
      }),
    }, {
      name: 'campaigns',
      path: '/campaigns',
    }, {
      name: 'emails',
      path: '/emails',
    }, {
      name: 'templates',
      path: '/templates',
    }, {
      name: 'edit_template',
      path: '/template/:id',
    }]
  },
  {
    name: 'filestore',
    path: '/filestore',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['storage:reader'],
      redirectName: 'filestore.section',
      params: {
        section: FILESTORE_TYPE_PRIVATE,
      }
    }),
    children: [{
      name: 'section',
      path: '/:section',
    }]
  },
  {
    name: 'network',
    path: '/network',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      redirectName: 'network.franchises',
    }),
    children: [{
      name: 'franchises',
      path: '/franchises',
      redirect: getRoleRedirectHandler({
        redirectName: 'network.franchises.list',
      }),
      children: [{
        name: 'list',
        path: '/list',
      }]
    }, {
      name: 'users',
      path: '/users',
      redirect: getRoleRedirectHandler({
        redirectName: 'network.users.list',
        orgParam: 'orgid',
      }),
      children: [{
        name: 'list',
        path: '/list',
      }]
    }, {
      name: 'admin',
      path: '/admin',
    }]
  },
  {
    name: 'audit',
    path: '/audit',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['rbac:event_viewer'],
      redirectName: 'audit.events',
      orgParam: 'orgid',
      scopeParam: 'scope',
    }),
    children: [{
      name: 'events',
      path: '/events',
    }]
  },
  {
    name: 'nopress',
    path: '/nopress',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['nopress:reader'],
      redirectName: 'nopress.org.website.page',
      orgParam: 'orgid',
      params: {
        type: 'section',
        id: 'root',
      },
      getAsyncParams: async (store) => {
        const dispatch = store.dispatch as ThunkDispatcher
        await dispatch(nopressActions.loadConfig())
        const config = nopressSelectors.config(store.getState())
        const website = config.websites[0]
        const websiteid = website ? website.id : 'unknown'
        return {
          websiteid,
        }
      }
    }),
    children: [{
      name: 'org',
      path: '/org/:orgid',
      children: [{
        name: 'website',
        path: '/website/:websiteid',
        children: [{
          name: 'page',
          path: '/edit/:type/:id'
        }]
      }]
    }]
  },
  {
    name: 'booking',
    path: '/booking',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['booking:reader'],
      redirectName: 'booking.bookings',
      orgParam: 'orgid',
      scopeParam: 'scope',
    }),
    children: [{
      name: 'calendar',
      path: '/calendar',
      redirect: getRoleRedirectHandler({
        roleNames: ['booking:reader'],
        redirectName: 'booking.calendar',
        orgParam: 'orgid',
        scopeParam: 'scope',
      }),
    }, {
      name: 'bookings',
      path: '/bookings',
      redirect: getRoleRedirectHandler({
        roleNames: ['booking:reader'],
        redirectName: 'booking.bookings',
        orgParam: 'orgid',
        scopeParam: 'scope',
      }),
    }, {
      name: 'availability',
      path: '/availability',
      redirect: getRoleRedirectHandler({
        roleNames: ['booking:reader'],
        redirectName: 'booking.availability',
        orgParam: 'orgid',
        scopeParam: 'scope',
      }),
    }, {
      name: 'pricing',
      path: '/pricing',
      redirect: getRoleRedirectHandler({
        roleNames: ['booking:reader'],
        redirectName: 'booking.pricing',
        orgParam: 'orgid',
        scopeParam: 'scope',
      }),
    }, {
      name: 'reporting',
      path: '/reporting',
      redirect: getRoleRedirectHandler({
        roleNames: ['booking:reader'],
        redirectName: 'booking.reporting',
        orgParam: 'orgid',
        scopeParam: 'scope',
      }),
    }, {
      name: 'config',
      path: '/config',
      redirect: getRoleRedirectHandler({
        roleNames: ['booking:reader'],
        redirectName: 'booking.config',
        orgParam: 'orgid',
        scopeParam: 'scope',
      }),
    }]
  },
  {
    name: 'marketing',
    path: '/marketing',
    auth: auth.user,
    redirect: getRoleRedirectHandler({
      roleNames: ['marketing:reader'],
      redirectName: 'marketing.calendar',
    }),
    children: [{
      name: 'campaigns',
      path: '/campaigns',
    }, {
      name: 'calendar',
      path: '/calendar',
    }, {
      name: 'posts',
      path: '/posts',
    }, {
      name: 'socials',
      path: '/socials',
    }, {
      name: 'settings',
      path: '/settings',
      redirect: getRoleRedirectHandler({
        roleNames: ['marketing:reader'],
        redirectName: 'marketing.settings.profile_form',
      }),
      children: [{
        name: 'profile_admin',
        path: '/profile_admin',
      }, {
        name: 'profile_form',
        path: '/profile_form',
      }, {
        name: 'schedule',
        path: '/schedule',
      }, {
        name: 'prompts',
        path: '/prompts',
      }]
    }]
  },
]

export default routes